import { newestFirst, type LibraryRow } from "./row.js";
import { corpusNamed, normaliseQuery, type CorpusAnswer } from "./search.js";

/**
 * The toolbar's two selects and its filter box, as one pure function.
 *
 * Filtering is a product rule, so it is here rather than inside a render. There is one order,
 * newest first, which the day groups need (issue 193); the sort control is gone.
 */

/** The date select's windows. `all` is what "Last 30 days" widens to when nothing is set. */
export type DateWindow = "all" | "today" | "week" | "month";

export interface LibraryFilters {
  /** A person's name, or `null` for everyone. */
  readonly person: string | null;
  readonly dateWindow: DateWindow;
  /**
   * The toolbar's search box. **V0 search filters this list in place** — the grouped-moments
   * results view is V1 and is banner-marked as deferred in the design bundle, so there is no
   * second screen and no route to one.
   *
   * It reads the person and the title, which are the two strings a row carries, **and** the
   * sessions the corpus named, which is how a word said out loud and written down nowhere
   * finds its session (issue 26). The second half arrives through `CorpusAnswer`, because a
   * transcript is not on a row and must not be.
   */
  readonly query: string;
}

export const DEFAULT_FILTERS: LibraryFilters = {
  person: null,
  dateWindow: "all",
  query: "",
};

/**
 * What "Clear the filter" leaves behind: everything that narrows the list goes (the query,
 * the person and the date window). A function so the rule is asserted without a browser.
 */
export function cleared(): LibraryFilters {
  return DEFAULT_FILTERS;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function withinWindow(nowMs: number, window: DateWindow, row: LibraryRow): boolean {
  if (window === "all") return true;
  const days = { today: 1, week: 7, month: 30 }[window];
  return row.startedAtMs > nowMs - days * DAY_MS;
}

/**
 * Does this row survive the search box?
 *
 * A blank box keeps everything and asks the corpus nothing. Otherwise it is a union: the
 * strings the row carries, or the sessions the corpus named for this same query. Neither
 * half is redundant — the corpus holds the title and the Summary and not the person, and the
 * row holds the person and the title and not a word of what was said.
 */
function matchesQuery(
  query: string,
  row: LibraryRow,
  answer: CorpusAnswer | null,
): boolean {
  const needle = normaliseQuery(query).toLowerCase();
  if (needle.length === 0) return true;
  return (
    row.person.toLowerCase().includes(needle) ||
    (row.title?.toLowerCase().includes(needle) ?? false) ||
    corpusNamed(answer, query, row.sessionId)
  );
}

/**
 * The toolbar, applied.
 *
 * **Search combines with the person and the date rather than replacing them**, which is one
 * `&&` and is the shape rather than a rule somebody has to remember: a search is a fourth
 * predicate on the same row, not a different list. An owner who has narrowed to one person
 * and then types a word stays narrowed to that person.
 *
 * `answer` is the corpus's reply, or `null` where there is none yet — see `search.ts`.
 */
export function applyFilters(
  nowMs: number,
  filters: LibraryFilters,
  rows: readonly LibraryRow[],
  answer: CorpusAnswer | null = null,
): LibraryRow[] {
  const kept = rows.filter(
    (row) =>
      (filters.person === null || row.person === filters.person) &&
      withinWindow(nowMs, filters.dateWindow, row) &&
      matchesQuery(filters.query, row, answer),
  );
  return newestFirst(kept);
}

/** The people the Person select offers: the ones this library actually holds. */
export function peopleIn(rows: readonly LibraryRow[]): string[] {
  return [...new Set(rows.map((row) => row.person))].sort((a, b) => a.localeCompare(b));
}
